import { Case } from "@/types";
import { getCase } from "@/utils/apiCalls";
import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthInfo } from "@propelauth/react";
import { Layout } from "@/components/Layout";
import { LoadingView } from "@/components/Loader";
import { InputDocumentTable } from "../components/table/InputDocumentTable";
import { OutputDocumentTable } from "../components/table/OutputDocumentTable";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BreadcrumbNav = (props: { caseName: string }) => {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href="/case/all">Cases</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{props.caseName}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export const IndividualCasePage = () => {
  const { caseId } = useParams();
  const authInfo = useAuthInfo();
  const [case_, setCase] = useState<Case | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get the active tab from URL hash, defaulting to "output-docs" if not set
  const activeTab = location.hash.slice(1) || "output-docs";

  useEffect(() => {
    setIsLoading(true);
    getCase(caseId ?? "", authInfo.accessToken ?? null).then((case_) => {
      if (case_ === null) {
        toast.error("Failed to fetch case");
      } else {
        setCase(case_);
      }
      setIsLoading(false);
    });
  }, [caseId]);

  const hasProcessingDocuments =
    case_?.input_documents.some(
      (document) => document.status === "processing"
    ) ?? false;

  useEffect(() => {
    if (!hasProcessingDocuments) return;

    let cancelled = false;
    const pollDocuments = async () => {
      const refreshedCase = await getCase(
        caseId ?? "",
        authInfo.accessToken ?? null
      );
      if (!cancelled && refreshedCase !== null) {
        setCase(refreshedCase);
      }
    };
    const intervalId = window.setInterval(pollDocuments, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [caseId, authInfo.accessToken, hasProcessingDocuments]);

  const handleTabChange = (value: string) => {
    navigate(`${location.pathname}#${value}`, { replace: true });
  };

  return (
    <Layout title="Case">
      {isLoading ? (
        <LoadingView text="Loading case..." />
      ) : (
        <>
          <BreadcrumbNav
            caseName={`${case_?.first_name} ${case_?.last_name}`}
          />
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className="grid w-[360px] grid-cols-2">
              <TabsTrigger value="output-docs">Output Documents</TabsTrigger>
              <TabsTrigger value="input-docs">Input Documents</TabsTrigger>
            </TabsList>
            <TabsContent value="output-docs">
              {case_ && <OutputDocumentTable case={case_} setCase={setCase} />}
            </TabsContent>
            <TabsContent value="input-docs">
              <InputDocumentTable
                caseId={caseId ?? ""}
                data={case_?.input_documents ?? []}
                setCase={setCase}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </Layout>
  );
};
